import {
	infiniteQueryOptions,
	queryOptions,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from '@tanstack/react-query'

import { fingerprint, successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import { AIR_ROOT_KEY } from '../query-keys'
import {
	CASE_LOGS_PAGE_SIZE,
	getCase,
	getCaseEvidence,
	getCaseList,
	getCaseLogs,
	getInvestigationHistory,
} from './api'

import type { CaseDetails } from './types'

/**
 * Query keys for Case Management.
 *
 * Tuples are byte-identical to the pre-split `airKeys.case*` — the split moved
 * where they are declared, never what they produce, so no cache entry is
 * orphaned and no `invalidateQueries` call changes meaning.
 */
export const caseKeys = {
	all: [...AIR_ROOT_KEY, 'cases'] as const,
	cases_list: (
		tenant: string,
		limit: number = 5,
		openedAfter?: string,
		openedBefore?: string,
	) =>
		[
			...caseKeys.all,
			'list',
			tenant,
			limit,
			openedAfter,
			openedBefore,
		] as const,
	case_details: (tenant: string, id: string) =>
		[...caseKeys.all, 'detail', tenant, id] as const,
	case_logs: (
		tenant: string,
		id: string,
		matched_events: CaseDetails['matched_events'],
	) =>
		[
			...caseKeys.case_details(tenant, id),
			'logs',
			// Fingerprint instead of the raw (potentially thousands-of-IDs) object —
			// keeps the Query devtools responsive while still busting cache if the
			// matched-event set changes for this case.
			fingerprint(matched_events),
		] as const,
	case_evidence: (tenant: string, id: string) =>
		[...caseKeys.case_details(tenant, id), 'evidence'] as const,
	investigation_history: (tenant: string, id: string) =>
		[...caseKeys.case_details(tenant, id), 'investigation_history'] as const,
}

/**
 * Query options for Case Management
 */
export const caseQueries = {
	cases_list: (
		tenant: string,
		limit: number = 5,
		openedAfter?: string,
		openedBefore?: string,
	) =>
		infiniteQueryOptions({
			queryKey: caseKeys.cases_list(tenant, limit, openedAfter, openedBefore),
			queryFn: ({ pageParam = 1 }) =>
				getCaseList(tenant, limit, pageParam, openedAfter, openedBefore),
			gcTime: time(1, 'm'),
			initialPageParam: 1,
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			getNextPageParam: (lastPage, _allPages, lastPageParam) => {
				if (lastPage.cases.length === 0) {
					return undefined
				}

				return lastPageParam + 1
			},
		}),
	case_details: (tenant: string, id: string) =>
		queryOptions({
			queryKey: caseKeys.case_details(tenant, id),
			queryFn: ({ signal }) => getCase(tenant, id, signal),
			gcTime: 0,
			staleTime: 0,
			// The app has no SSE/sockets — run-state cards POLL. Refetch the case
			// detail while the investigation job is still in flight
			// (pending/running/paused) so the Behavior run-state card advances;
			// stop once the job reaches a terminal state (completed/failed) or
			// there is no investigation. No blanket staleTime is added.
			refetchInterval: (query) => {
				const status = query.state.data?.investigation?.status
				if (
					status === 'pending' ||
					status === 'running' ||
					status === 'paused'
				) {
					return time(15, 's')
				}
				return false
			},
		}),
	case_evidence: (tenant: string, id: string) =>
		queryOptions({
			queryKey: caseKeys.case_evidence(tenant, id),
			queryFn: ({ signal }) => getCaseEvidence(tenant, id, signal),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
			// Unwrap live envelope `{ evidence: [] }` OR bare-array fixture.
			select: (data) => (Array.isArray(data) ? data : (data.evidence ?? [])),
		}),
	investigation_history: (tenant: string, id: string) =>
		queryOptions({
			queryKey: caseKeys.investigation_history(tenant, id),
			queryFn: ({ signal }) => getInvestigationHistory(tenant, id, signal),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
			// Unwrap live envelope OR bare-array fixture.
			select: (data) =>
				Array.isArray(data) ? data : (data.investigation_history ?? []),
		}),
	case_logs: (
		tenant: string,
		id: string,
		matched_events: CaseDetails['matched_events'],
	) =>
		infiniteQueryOptions({
			queryKey: caseKeys.case_logs(tenant, id, matched_events),
			queryFn: ({ pageParam = 1 }) =>
				getCaseLogs(tenant, matched_events, pageParam),
			gcTime: time(3, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			initialPageParam: 1,
			getNextPageParam: (lastPage, _allPages, lastPageParam) => {
				// SearchLogResponse always carries `hits`; only the page-size
				// shortfall tells us we've reached the last page.
				if (lastPage.hits.hits.length < CASE_LOGS_PAGE_SIZE) return undefined
				return lastPageParam + 1
			},
			select: (data) => ({
				hits: data.pages.flatMap((page) => page.hits.hits),
			}),
		}),
}

/**
 * Use cases list
 * @param limit - The limit of cases to return
 * @param openedAfter - Only return cases opened at/after this time (inclusive, ISO)
 * @param openedBefore - Only return cases opened before this time (exclusive, ISO)
 */
export const useCasesList = (
	limit: number = 25,
	openedAfter?: string,
	openedBefore?: string,
) => {
	const tenant = useCurrentTenant()

	return useSuspenseInfiniteQuery(
		caseQueries.cases_list(tenant, limit, openedAfter, openedBefore),
	)
}

/**
 * Use case details
 * @param id - The case ID
 */
export const useCaseDetails = (id: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(caseQueries.case_details(tenant, id))
}

/**
 * Use case evidence (curated findings backing the verdict). Lazy — call only
 * from the Evidence tab route so it fetches on tab open. Returns the unwrapped
 * `Array<Evidence>` (tolerates wrapped `{ evidence: [] }` + bare-array fixture).
 * @param id - The case ID
 */
export const useCaseEvidence = (id: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(caseQueries.case_evidence(tenant, id))
}

/**
 * Use investigation history (full "show your work" superset). Lazy — call only
 * from the Investigation Steps tab route. Returns the unwrapped
 * `Array<InvestigationHistoryItem>` (tolerates wrapped + bare-array fixture).
 * @param id - The case ID
 */
export const useInvestigationHistory = (id: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(caseQueries.investigation_history(tenant, id))
}

/**
 * Use case logs (paginated infinite query against OpenSearch)
 * @param id - The case ID
 * @param matched_events - The matched events
 */
export const useCaseLogs = (
	id: string,
	matched_events: CaseDetails['matched_events'],
) => {
	const tenant = useCurrentTenant()

	const query = useSuspenseInfiniteQuery(
		caseQueries.case_logs(tenant, id, matched_events),
	)

	return {
		hits: query.data.hits,
		fetchNextPage: query.fetchNextPage,
		hasNextPage: query.hasNextPage,
		isFetchingNextPage: query.isFetchingNextPage,
		isFetching: query.isFetching,
	}
}
